import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Skeleton } from '@monksflow/monks-ui';
import clsx from 'clsx';
import { useDelayedFlag } from '@/shared/hooks/useDelayedFlag';

type SettledResult = { src: string; status: 'loaded' | 'failed' };

/**
 * How long a fetch gets before the slot stops waiting for it.
 *
 * A stalled request fires neither `load` nor `error`, so nothing else here will
 * ever hear about it. Long enough that a thumbnail on a slow connection still
 * arrives; short enough that a column of them is visibly broken rather than
 * visibly slow, which is the difference between a board someone reports and a
 * board everyone works around.
 */
export const THUMBNAIL_LOAD_TIMEOUT_MS = 10_000;

export interface ThumbnailImageProps {
  src: string;
  alt: string;
  /** Sizes the box the image fills. */
  className?: string;
  fit?: 'cover' | 'contain';
  /** Takes the slot when the image cannot be fetched. */
  fallback?: ReactNode;
}

/**
 * An asset preview that says it is coming.
 *
 * A grid of these fetches full-size assets over the network, so a slow slot
 * holds a placeholder rather than sitting empty and then popping. A fast one
 * does not: a grid remounts its cards on every folder switch, and a thumbnail
 * the browser answers from its cache would otherwise replay the placeholder and
 * the fade on every card at once.
 *
 * The settled result carries the URL it describes, because presigned URLs are
 * re-signed on every fetch of the same asset. A success is deliberately sticky
 * across a re-sign, so the image on screen stays up instead of flashing the
 * placeholder; a failure is scoped to the signature that failed, so the next
 * one gets a real attempt rather than the fallback forever.
 */
export function ThumbnailImage(props: ThumbnailImageProps) {
  const [settled, setSettled] = useState<SettledResult | null>(null);

  // Read through a ref rather than a dependency: re-running the ref callback on
  // every src swap would settle the new URL off the outgoing element's state.
  const currentSrc = useRef(props.src);
  currentSrc.current = props.src;

  // State rather than a ref, so the observer below re-runs on every attach. A
  // failure unmounts the `<img>` to make room for the fallback, and the next
  // signature mounts a new one; a ref would leave the observer bound to the
  // element that went away, which can no longer intersect anything.
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  // A cached image can already be complete before React attaches the handlers
  // below, and its load event is then long gone. Without this the placeholder
  // would never leave.
  const readSettledStatus = useCallback((node: HTMLImageElement | null) => {
    setImage(node);
    if (!node?.complete) return;
    setSettled({ src: currentSrc.current, status: node.naturalWidth > 0 ? 'loaded' : 'failed' });
  }, []);

  // The clock below measures a fetch, and a lazy image below the fold is not
  // being fetched yet. Started at mount it would condemn every row of a long
  // grid the viewer has not reached, and the glyph would still be there when
  // they arrived. Where there is no observer to ask, assume visible: that is
  // the old behaviour of an eager image, not a slot that never times out.
  const [inView, setInView] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (inView || !image) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setInView(true);
    });
    observer.observe(image);
    return () => observer.disconnect();
  }, [inView, image]);

  // Nothing fires when a fetch stalls, so the bound has to come from here. It
  // only reaches a slot that is still shimmering: an image already on screen
  // outlives a re-sign that never answers, rather than being replaced by the
  // fallback for a URL the viewer never needed.
  useEffect(() => {
    if (!inView) return;

    const timer = setTimeout(() => {
      setSettled((previous) => {
        if (previous?.status === 'loaded' || previous?.src === currentSrc.current) return previous;
        return { src: currentSrc.current, status: 'failed' };
      });
    }, THUMBNAIL_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [props.src, inView]);

  // A success outlives the URL that produced it, so a re-sign leaves the image
  // on screen. A failure does not: it belongs to the signature that failed, and
  // the next one gets a real attempt.
  const isLoaded = settled?.status === 'loaded';
  const hasFailed = settled?.status === 'failed' && settled.src === props.src;

  const showSkeleton = useDelayedFlag(!isLoaded && !hasFailed);

  // Sticky, because the delayed flag drops a render after the image arrives:
  // read directly, it would strip the transition and cancel the fade it started.
  const [fadesIn, setFadesIn] = useState(false);
  useEffect(() => {
    if (showSkeleton) setFadesIn(true);
  }, [showSkeleton]);

  if (hasFailed) return <>{props.fallback}</>;

  return (
    <span className={clsx('relative block overflow-hidden', props.className)}>
      {!isLoaded && showSkeleton && (
        <Skeleton className="absolute inset-0" data-testid="thumbnail-placeholder" />
      )}
      <img
        ref={readSettledStatus}
        src={props.src}
        alt={props.alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setSettled({ src: props.src, status: 'loaded' })}
        onError={() => setSettled({ src: props.src, status: 'failed' })}
        className={clsx(
          'h-full w-full',
          fadesIn && 'transition-opacity duration-base ease-out',
          props.fit === 'contain' ? 'object-contain' : 'object-cover',
          isLoaded ? 'opacity-100' : 'opacity-0',
        )}
      />
    </span>
  );
}
